import axios from "axios"
import configDetails from "../Config/Config"
import { Auth } from 'aws-amplify';
import { loadStripe } from "@stripe/stripe-js"

const TIER_RANKS = {
  'BAS': 1,  // Basic
  'PRO': 2,  // Professional
  'ENT': 3   // Enterprise
};

const DURATION_RANKS = {
  'MONTHLY': 1,
  'ANNUAL': 2,
  'YEARLY': 2  // Support both ANNUAL and YEARLY naming conventions
};

const normalizePlanCode = (planCode) => {
  if (!planCode) return '';
  return planCode.replace('-ANN', '').toUpperCase();
};

const getTierRank = (planCode) => {
  const normalized = normalizePlanCode(planCode);
  return TIER_RANKS[normalized] || 0;
};

const getDurationRank = (planType) => {
  return DURATION_RANKS[planType?.toUpperCase()] || 0;
};

const validateSubscriptionTransition = (currentSubscription, requestedPlan) => {
  // No existing subscription - allow any plan
  if (!currentSubscription || !currentSubscription.planCode) {
    return {
      allowed: true,
      reason: 'New subscription'
    };
  }

  const currentTierRank = getTierRank(currentSubscription.planCode);
  const currentDurationRank = getDurationRank(currentSubscription.planType);
  
  const requestedTierRank = getTierRank(requestedPlan.planCode);
  const requestedDurationRank = getDurationRank(requestedPlan.billingCycle);

  // Check for duplicate plan
  if (currentTierRank === requestedTierRank && currentDurationRank === requestedDurationRank) {
    return {
      allowed: false,
      reason: 'Cannot subscribe to the same plan'
    };
  }

  // Determine transitions
  const isTierUpgrade = requestedTierRank > currentTierRank;
  const isTierDowngrade = requestedTierRank < currentTierRank;
  const isDurationUpgrade = requestedDurationRank > currentDurationRank;
  const isDurationDowngrade = requestedDurationRank < currentDurationRank;

  // Tier Upgrade - always allowed
  if (isTierUpgrade && !isDurationDowngrade) {
    return {
      allowed: true,
      reason: 'Tier upgrade'
    };
  }

  // Duration Upgrade - always allowed
  if (isDurationUpgrade && !isTierDowngrade) {
    return {
      allowed: true,
      reason: 'Duration upgrade'
    };
  }

  // Tier Upgrade + Duration Upgrade - allowed
  if (isTierUpgrade && isDurationUpgrade) {
    return {
      allowed: true,
      reason: 'Tier and duration upgrade'
    };
  }

  // Tier Downgrade + Duration Upgrade - allowed (overall value still increases)
  if (isTierDowngrade && isDurationUpgrade) {
    return {
      allowed: true,
      reason: 'Duration upgrade (compensates tier downgrade)'
    };
  }

  // Tier Downgrade without Duration Upgrade - NOT allowed
  if (isTierDowngrade && !isDurationUpgrade) {
    const normalizedCurrent = normalizePlanCode(currentSubscription.planCode);
    const normalizedRequested = normalizePlanCode(requestedPlan.planCode);
    return {
      allowed: false,
      reason: `Cannot downgrade plan tier while subscription is active. Existing: ${normalizedCurrent} ${currentSubscription.planType}, Requested: ${normalizedRequested} ${requestedPlan.billingCycle}`
    };
  }

  // Duration Downgrade - NOT allowed
  if (isDurationDowngrade) {
    return {
      allowed: false,
      reason: `Cannot downgrade billing cycle. Existing: ${currentSubscription.planType}, Requested: ${requestedPlan.billingCycle}`
    };
  }

  return {
    allowed: false,
    reason: 'Invalid subscription transition'
  };
};

/**
 * Get detailed transition information for logging/analytics
 * @param {object} currentSubscription - { planCode, planType }
 * @param {object} requestedPlan - { planCode, billingCycle }
 * @returns {object} Transition details
 */
const getTransitionDetails = (currentSubscription, requestedPlan) => {
  const validation = validateSubscriptionTransition(currentSubscription, requestedPlan);
  
  const normalizedCurrent = normalizePlanCode(currentSubscription?.planCode);
  const normalizedRequested = normalizePlanCode(requestedPlan?.planCode);
  
  return {
    ...validation,
    transition: {
      from: currentSubscription ? `${normalizedCurrent} ${currentSubscription.planType}` : 'None',
      to: `${normalizedRequested} ${requestedPlan.billingCycle}`,
      tierChange: currentSubscription ? getTierRank(requestedPlan.planCode) - getTierRank(currentSubscription.planCode) : 'new',
      durationChange: currentSubscription ? getDurationRank(requestedPlan.billingCycle) - getDurationRank(currentSubscription.planType) : 'new'
    }
  };
};

let stripePromise = null;

// Initialize Stripe with error checking
const getStripe = () => {
  if (!stripePromise) {
    const key = configDetails.stripePublicKey;
    if (!key) {
      throw new Error("Stripe public key is not configured");
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
};

const createPaymentIntent = async (paymentData) => {
  let token;
  try {
    const currentUser = await Auth.currentAuthenticatedUser();
    token = currentUser.signInUserSession.idToken.jwtToken;
  } catch (error) {
    throw new Error("Authorization token missing. Please log in.");
  }

  try {
    console.log("📦 Creating payment intent with data:", paymentData);

    const response = await axios.post(
      `${configDetails.apiBaseUrlPaymentservice}${configDetails.createStripePaymentEndpoint}`,
      {
        planId: paymentData.planId,
        pricingId: paymentData.pricingId,
        userEmail: paymentData.userEmail,
        userId: paymentData.userId,
        quantity: paymentData.quantity || 1,
        billingName: paymentData.billingName,
        billingEmail: paymentData.billingEmail,
        billingAddress: paymentData.billingAddress,
        billingCity: paymentData.billingCity,
        billingState: paymentData.billingState,
        billingCountry: paymentData.billingCountry,
        billingPostalCode: paymentData.billingPostalCode,
        notes: paymentData.notes,
        taxAmount: paymentData.taxAmount || 0,
        currency: paymentData.currency
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log("✅ Payment intent response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Payment service error:", error.response?.data || error);
    throw new Error(
      error.response?.data?.message || 
      error.message || 
      "Failed to create payment intent"
    );
  }
}

export const PaymentService = {
  createPaymentIntent,
  getStripe,
  validateSubscriptionTransition,
  getTierRank,
  getDurationRank,
  getTransitionDetails
}

export default PaymentService